// src/pages/JobSearchGantt.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { supabase } from '../utils/supabase';
import PageHeader from '../components/PageHeader';
import JobApplicationEnhancedPanel from '../components/JobApplicationEnhancedPanel';
import JobSearchMetrics from '../components/JobSearchMetrics';
import JobSearchFilters from '../components/JobSearchFilters';
import JobSearchTimeline from '../components/JobSearchTimeline';
import JobSearchBulkActions from '../components/JobSearchBulkActions';
import { JobApplicationEnhanced, ApplicationStatus } from '../types/jobSearch';
import { IoGridOutline, IoListOutline } from 'react-icons/io5';
import { createLogger } from '../utils/logger';
import { useBusinessMetrics } from '../hooks/useStatsigFeatures';
import { useAuthState } from '../hooks/useAuthState';
import useTouchScroll from '../hooks/useTouchScroll';

const logger = createLogger({ module: 'JobSearchGantt', level: 'info' });

// Generate a UUID using a cross-platform method
const generateUUID = () => {
    // If native crypto.randomUUID exists, use it
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    
    // Fallback to a simple UUID generation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

interface JobSearchGanttProps {
    initialApplications?: JobApplicationEnhanced[];
}

const JobSearchGantt: React.FC<JobSearchGanttProps> = ({
    initialApplications = []
}) => {
    const { darkMode } = useDarkMode();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { user, isAuthenticated } = useAuthState();
    const { trackFeatureUsage, trackUserJourney } = useBusinessMetrics();
    const [applications, setApplications] = useState<JobApplicationEnhanced[]>(initialApplications);
    const [selectedApplication, setSelectedApplication] = useState<JobApplicationEnhanced | null>(null);
    const [selectedApplications, setSelectedApplications] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(initialApplications.length === 0);
    const [viewMode, setViewMode] = useState<'timeline' | 'kanban'>('timeline');
    const [showArchived, setShowArchived] = useState(false);
    const [filters, setFilters] = useState({
        tags: [] as string[],
        statuses: [] as ApplicationStatus[],
        dateRange: {
            start: null as Date | null,
            end: null as Date | null
        }
    });
    const [pageJourneyId, setPageJourneyId] = useState<string | null>(null);
    
    // Add ref for scrollable container
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    
    // Apply touch scroll handling
    useTouchScroll(scrollContainerRef);

    // Track page view and start journey
    useEffect(() => {
        const startTime = performance.now();
        
        // Start job search journey
        const journeyId = `job_search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setPageJourneyId(journeyId);
        
        // Track page view
        trackFeatureUsage('job_search_page', 'viewed', {
            journey_id: journeyId
        });
        
        trackUserJourney('job_search_page_started', {
            journey_id: journeyId,
            entry_point: 'direct'
        });
        
        // Track performance
        trackFeatureUsage('page_performance', 'measured', {
            journey_id: journeyId,
            route: '/job-search',
            load_time_ms: Math.round(performance.now() - startTime)
        });
        
        return () => {
            // End journey when leaving page
            if (pageJourneyId) {
                trackUserJourney('job_search_page_completed', {
                    journey_id: pageJourneyId,
                    outcome: 'journey_complete'
                });
            }
        };
    }, []); // Empty dependency array - this should only run once on mount

    // Fetch applications only if needed (when showing archived or if no initial data)
    useEffect(() => {
        // If showing archived data, we need to fetch it regardless
        if (showArchived) {
            fetchApplications();
            return;
        }
        
        // If we have initialApplications and we're not showing archived, only fetch
        // if we're reloading after user changes (to refresh the data)
        if (initialApplications.length > 0 && !showArchived && applications === initialApplications) {
            logger.debug('Using SSR data for job applications');
            setIsLoading(false);
        } else {
            fetchApplications();
        }
    }, [showArchived, user]);

    const fetchApplications = async () => {
        try {
            // Use user from useAuthState hook
            if (!user) return;

            setIsLoading(true);
            
            const { data, error } = await supabase
                .from('job_applications_enhanced')
                .select('*')
                .eq('user_id', user.id)
                .eq('is_active', !showArchived)
                .order('application_date', { ascending: false });

            if (error) throw error;

            setApplications(data || []);
            logger.debug(`Fetched ${data?.length} job applications, showArchived=${showArchived}`);
        } catch (error) {
            logger.error('Error fetching applications:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Track view mode change
    const handleViewModeChange = (mode: 'timeline' | 'kanban') => {
        setViewMode(mode);
        
        trackFeatureUsage('job_search_view_mode', 'changed', {
            journey_id: pageJourneyId,
            mode
        });
    };

    // Filter applications based on selected filters
    const filteredApplications = applications.filter(app => {
        // Filter by tags
        if (filters.tags.length > 0 && (!app.tags || !filters.tags.every(tag => app.tags.includes(tag)))) {
            return false;
        }
        
        // Filter by status
        if (filters.statuses.length > 0 && !filters.statuses.includes(app.status as ApplicationStatus)) {
            return false;
        }
        
        // Filter by date range
        if (filters.dateRange.start && new Date(app.application_date) < filters.dateRange.start) {
            return false;
        }
        
        if (filters.dateRange.end) {
            const endDate = new Date(filters.dateRange.end);
            endDate.setHours(23, 59, 59, 999); // End of day
            if (new Date(app.application_date) > endDate) {
                return false;
            }
        }
        
        return true;
    });

    // Handle bulk actions
    const handleBulkStatusUpdate = async (status: ApplicationStatus) => {
        try {
            const { error } = await supabase.rpc('update_job_application_status', {
                p_application_ids: selectedApplications,
                p_status: status,
                p_timestamp: new Date().toISOString()
            });

            if (error) throw error;

            fetchApplications();
            setSelectedApplications([]);
        } catch (error) {
            logger.error('Error updating applications:', error);
        }
    };

    const handleBulkAddTags = async (tagsToAdd: string[]) => {
        try {
            const { error } = await supabase.rpc('add_job_application_tags', {
                p_application_ids: selectedApplications,
                p_tags: tagsToAdd
            });

            if (error) throw error;

            fetchApplications();
            setSelectedApplications([]);
        } catch (error) {
            logger.error('Error adding tags:', error);
        }
    };

    const handleBulkRemoveTags = async (tagsToRemove: string[]) => {
        try {
            const { error } = await supabase.rpc('remove_job_application_tags', {
                p_application_ids: selectedApplications,
                p_tags: tagsToRemove
            });

            if (error) throw error;

            fetchApplications();
            setSelectedApplications([]);
        } catch (error) {
            logger.error('Error removing tags:', error);
        }
    };

    const handleBulkArchive = async () => {
        try {
            const { error } = await supabase
                .from('job_applications_enhanced')
                .update({ is_active: false })
                .in('id', selectedApplications);

            if (error) throw error;

            fetchApplications();
            setSelectedApplications([]);
        } catch (error) {
            logger.error('Error archiving applications:', error);
        }
    };

    const handleSaveApplication = async (application: JobApplicationEnhanced) => {
        try {
            // Use user from useAuthState hook
            if (!user) throw new Error('No user found');

            const { error } = await supabase
                .from('job_applications_enhanced')
                .upsert({
                    ...application,
                    user_id: user.id,
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;
            
            // Refetch applications after successful save
            await fetchApplications();
            setSelectedApplication(null); // Close the panel after saving
        } catch (error) {
            logger.error('Error saving application:', error);
            throw error;
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            <div className="sticky top-0 z-40">
                <PageHeader
                    title="Job Search"
                    buttons={
                        <div className="flex items-center gap-2 flex-row">
                            <button
                                onClick={() => setShowArchived(!showArchived)}
                                className="whitespace-normal text-xs md:text-sm px-2 py-1.5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 min-w-0 shrink"
                            >
                                {showArchived ? 'Show Active' : 'Show Archived'}
                            </button>
                            <button
                                onClick={() => {
                                    // Create a new empty application
                                    const newApp: JobApplicationEnhanced = {
                                        id: generateUUID(),
                                        user_id: user?.id || '',
                                        company: '',
                                        job_title: '',
                                        status: 'applied',
                                        status_history: [{
                                            status: 'applied',
                                            timestamp: new Date().toISOString()
                                        }],
                                        application_date: new Date().toISOString(),
                                        created_at: new Date().toISOString(),
                                        updated_at: new Date().toISOString(),
                                        last_status_change: new Date().toISOString(),
                                        is_active: true,
                                        notes: '',
                                        tags: [],
                                        interviews: []
                                    };
                                    setSelectedApplication(newApp);
                                    trackFeatureUsage('create_job_application', 'clicked', {
                                        journey_id: pageJourneyId
                                    });
                                }}
                                className="whitespace-normal text-xs md:text-sm px-2 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 min-w-0 shrink"
                            >
                                Create Application
                            </button>
                        </div>
                    }
                />
            </div>
            
            <div className="flex-1 relative overflow-hidden">
                <div 
                    ref={scrollContainerRef}
                    className="h-full overflow-y-auto pt-[72px]"
                >
                    <div className="container mx-auto px-4 pb-8 mt-4">
                        
                        <div className="mb-8">
                            <JobSearchMetrics 
                                applications={applications} 
                                timeRange="all"
                            />
                        </div>
                        
                        <div className="mb-4">
                            <JobSearchFilters
                                availableTags={Array.from(new Set(applications.flatMap(app => app.tags || [])))}
                                selectedTags={filters.tags}
                                selectedStatuses={filters.statuses}
                                dateRange={filters.dateRange}
                                onTagsChange={tags => setFilters(f => ({ ...f, tags }))}
                                onStatusesChange={statuses => setFilters(f => ({ ...f, statuses }))}
                                onDateRangeChange={dateRange => setFilters(f => ({ ...f, dateRange }))}
                            />
                        </div>
                        
                        {selectedApplications.length > 0 && (
                            <div className="mb-4">
                                <JobSearchBulkActions
                                    selectedApplications={applications.filter(app => selectedApplications.includes(app.id))}
                                    onSelectAll={() => setSelectedApplications(applications.map(app => app.id))}
                                    onDeselectAll={() => setSelectedApplications([])}
                                    onArchiveSelected={handleBulkArchive}
                                    onUpdateStatus={handleBulkStatusUpdate}
                                    onAddTags={handleBulkAddTags}
                                    onRemoveTags={handleBulkRemoveTags}
                                    availableTags={Array.from(
                                        new Set(
                                            applications
                                                .flatMap(app => app.tags || [])
                                                .filter(Boolean)
                                        )
                                    )}
                                />
                            </div>
                        )}
                        
                        <JobSearchTimeline
                            applications={filteredApplications}
                            onApplicationClick={(app) => {
                                setSelectedApplication(app);
                                
                                trackFeatureUsage('view_job_application', 'clicked', {
                                    journey_id: pageJourneyId,
                                    application_id: app.id,
                                    company: app.company
                                });
                            }}
                            selectedApplications={selectedApplications}
                            setSelectedApplications={setSelectedApplications}
                        />
                    </div>
                </div>
            </div>
            
            {selectedApplication && (
                <JobApplicationEnhancedPanel
                    application={selectedApplication}
                    isOpen={selectedApplication !== null}
                    onClose={() => setSelectedApplication(null)}
                    onSave={handleSaveApplication}
                />
            )}
        </div>
    );
};

export default JobSearchGantt;